import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import MaskedReveal from "@/components/site/MaskedReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  bag: "Piece 01 / Object",
  sunglasses: "Piece 02 / Optic",
  case: "Piece 03 / Vessel",
};

// Maps capsule item ids to server-side product ids.
const BUYABLE = {
  "c01-x-bag": "c01-x-bag",
  "c01-optic": "c01-optic",
};

async function beginCheckout(productId) {
  try {
    const origin = window.location.origin;
    const { data } = await axios.post(`${API}/checkout`, {
      item_id: productId,
      origin_url: origin,
    });
    if (!data?.url) throw new Error("No checkout URL");
    window.location.href = data.url;
  } catch (err) {
    const msg =
      err?.response?.data?.detail || "Could not open checkout. Try again.";
    toast.error(msg);
  }
}

function ItemFrame({ item, size = "large", index = 0 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  const objectClass =
    item.category === "case" ? "object-contain p-6" : "object-cover";

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: index * 0.1 }}
      className="group relative"
      data-testid={`capsule-item-${item.category}`}
    >
      <div
        className={`frame relative w-full overflow-hidden bg-[#0a0a0a] ${
          size === "large" ? "aspect-[4/5]" : "aspect-square"
        }`}
      >
        <motion.img
          style={{ y }}
          src={item.image}
          alt={item.name}
          className={`w-full h-[112%] ${objectClass}`}
        />
        {/* Spotlight vignette */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_35%,_rgba(0,0,0,0.55)_100%)]" />
        <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
          {ROLE_LABELS[item.category] || "Piece"}
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
          <span>{item.id.replace("c01-", "").toUpperCase()}</span>
          <span>{item.price === 0 ? "INCLUDED" : `$${item.price.toLocaleString()}`}</span>
        </div>
      </div>
      <div className="mt-4 flex items-baseline justify-between">
        <h4 className="font-display uppercase text-xl md:text-2xl tracking-tight">
          {item.name}
        </h4>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
          {size === "large" ? "FEATURED" : "COMPANION"}
        </span>
      </div>
      <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-md">
        {item.description}
      </p>
      {BUYABLE[item.id] ? (
        <button
          onClick={() => beginCheckout(BUYABLE[item.id])}
          className="btn-sharp mt-6"
          data-testid={`capsule-buy-${item.category}`}
        >
          <span>Acquire — ${item.price.toLocaleString()}</span>
        </button>
      ) : (
        <div
          className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50"
          data-testid={`capsule-included-${item.category}`}
        >
          Included with Optic 001
        </div>
      )}
    </motion.div>
  );
}

function DetailFrame({ item, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: index * 0.1 }}
      className="frame relative aspect-[4/3] w-full overflow-hidden"
      data-testid={`capsule-detail-${index}`}
    >
      <motion.img
        style={{ y }}
        src={item.image}
        alt={item.name}
        className="w-full h-[110%] object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />
      <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.3em] text-white/70">
        Detail — {String(index + 1).padStart(2, "0")}
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex items-baseline justify-between font-mono text-[9px] uppercase tracking-[0.25em] text-white/80">
        <span>{item.name}</span>
        <span>R.I.V.H™</span>
      </div>
    </motion.div>
  );
}

function Countdown({ target }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const compute = () => {
      const diff = Math.max(0, new Date(target).getTime() - Date.now());
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setT({ d, h, m, s });
    };
    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [target]);

  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div
      className="grid grid-cols-4 gap-4 md:gap-8 font-mono text-white"
      data-testid="capsule-countdown"
    >
      {[
        ["Days", t.d],
        ["Hours", t.h],
        ["Min", t.m],
        ["Sec", t.s],
      ].map(([label, val]) => (
        <div key={label} className="border-t border-white/20 pt-3">
          <div className="text-3xl md:text-5xl tabular-nums tracking-tight">
            {pad(val)}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white/50">
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Capsule() {
  const [capsule, setCapsule] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/capsule`)
      .then((r) => setCapsule(r.data))
      .catch((e) => console.error("capsule fetch", e));
  }, []);

  if (!capsule) {
    return (
      <section
        id="capsule"
        className="min-h-[60vh] flex items-center justify-center bg-black text-white/40 font-mono text-xs uppercase tracking-widest"
        data-testid="capsule-loading"
      >
        Loading capsule…
      </section>
    );
  }

  const bag = capsule.items.find((i) => i.category === "bag");
  const optic = capsule.items.find((i) => i.category === "sunglasses");
  const caseItem = capsule.items.find((i) => i.category === "case");
  const details = capsule.details || [];

  return (
    <section
      id="capsule"
      data-testid="capsule-section"
      className="relative py-32 md:py-40 px-6 md:px-10 bg-black text-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
              {capsule.code} /{" "}
              {new Date(capsule.release_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}{" "}
              / FIND YOUR PURPOSE
            </div>
            <MaskedReveal
              as="h2"
              trigger="view"
              className="font-display text-[19vw] md:text-[13vw] uppercase leading-[0.82] tracking-[-0.04em]"
              lines={[capsule.title]}
            />
          </div>
          <div className="md:col-span-4">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              {capsule.subtitle}
            </p>
          </div>
        </div>

        {/* Bento museum grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
          <div className="md:col-span-7 md:row-span-2">
            {bag && <ItemFrame item={bag} size="large" index={0} />}
          </div>
          <div className="md:col-span-5">
            {optic && <ItemFrame item={optic} size="small" index={1} />}
          </div>
          <div className="md:col-span-5 md:col-start-8">
            {caseItem && <ItemFrame item={caseItem} size="small" index={2} />}
          </div>
        </div>

        {/* Editorial detail atlas */}
        {details.length > 0 && (
          <div
            className="mt-24 md:mt-32 border-t border-white/15 pt-14"
            data-testid="capsule-details"
          >
            <div className="flex items-baseline justify-between mb-10">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                § Detail atlas — the manifesto, printed on the pieces
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                {String(details.length).padStart(2, "0")} plates
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {details.map((d, i) => (
                <DetailFrame key={d.id} item={d} index={i} />
              ))}
            </div>

            <p className="mt-8 text-center font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">
              Real / Intelligent / Valuable / Humans — engraved, printed, worn.
            </p>
          </div>
        )}

        {/* Release strip */}
        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end border-t border-white/15 pt-10">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
              Doors open in
            </div>
            <MaskedReveal
              as="h3"
              trigger="view"
              className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-none"
              lines={["Enter the drop"]}
            />
          </div>
          <div className="md:col-span-5 md:col-start-6">
            <Countdown target={capsule.release_at} />
          </div>
          <div className="md:col-span-3 md:col-start-10 flex flex-col md:items-end gap-3">
            <button
              onClick={() => beginCheckout("c01-bundle")}
              className="btn-sharp"
              data-testid="capsule-buy-bundle"
            >
              <span>Acquire Full Capsule — $495 →</span>
            </button>
            <a
              href="#register"
              className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors border-b border-white/25 pb-1"
              data-testid="capsule-register-cta"
            >
              or Register for the drop →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
